/**
 * What general Clu's one-off plays show in the chat, read the way the
 * backend writes them (`cleanse_api/copilot/one_off_runs.py`): the three
 * confirmation cards (a play run once, a live run of an agent, a one-off made
 * to repeat), the run a confirmed card started, the agent a repeat made, and
 * the draft agent a chat made for work only an agent runs.
 */
import { type IconDefinition } from '@/components/ui/app-icon';
import {
  iconArrowsRotate,
  iconFlask,
  iconPlay,
  iconPlug,
  iconRobot,
  iconSitemap,
} from '@/lib/icons';
import {
  sequenceChangeWords,
  sequenceStepSummary,
} from '@/containers/playbooks/custom-builder/sequence-step.util';
import {
  partDisplayType,
  type ThreadPartLike,
} from '@/components/agent-chat/question-card.util';
import type { ReviewCardItem } from '@/types/gtm-agents';
import {
  reviewCardChanges,
  type ReviewCardChangeLine,
} from '@/containers/agents/review-card.util';
import type {
  AddToSequenceConfig,
  CustomGraph,
  CustomNodeType,
  CustomRunDetail,
  CustomRunNodeStats,
} from '@/types/custom-playbooks';
import {
  EMPLOYMENT_CHECK_NAME,
  EMPLOYMENT_CHECK_SKILL_ID,
} from '@/utils/employment-check.util';
import { asRecord } from '@/components/agent-chat/as-record.util';

/** The confirmed actions whose cards are drawn here. */
export const ONE_OFF_ACTIONS: ReadonlySet<string> = new Set([
  'run_one_off_play',
  'run_agent_now',
  'repeat_one_off',
  'create_draft_agent',
]);

/** The displays of what a confirmed card did. */
export const ONE_OFF_RUN_STARTED = 'one_off_run_started';
export const AGENT_RUN_STARTED = 'agent_run_started';
export const REPEAT_AGENT_CREATED = 'repeat_agent_created';
export const DRAFT_AGENT_CREATED = 'draft_agent_created';
export const ONE_OFF_DISPLAYS = [
  ONE_OFF_RUN_STARTED,
  AGENT_RUN_STARTED,
  REPEAT_AGENT_CREATED,
  DRAFT_AGENT_CREATED,
] as const;

/** The message the run card's Review results sends: it names its run by
 * the start of the id, which Clu's `get_run_results` resolves in the chat,
 * so an earlier card never reads back the chat's latest run. */
export function reviewResultsMessage(runId: string): string {
  return `Review the results of run ${runId.slice(0, 8)}`;
}

/** The one-off tools as steps in the chat. */
export const ONE_OFF_TOOL_STEPS: Record<
  string,
  { done: string; running: string; icon: IconDefinition }
> = {
  list_crm_views: {
    done: 'Read the CRM views',
    running: 'Reading the CRM views',
    icon: iconPlug,
  },
  list_gtm_agents: {
    done: 'Read your agents',
    running: 'Reading your agents',
    icon: iconRobot,
  },
  get_run_results: {
    done: 'Read the run results',
    running: 'Reading the run results',
    icon: iconFlask,
  },
  request_one_off_run: {
    done: 'Prepared the run',
    running: 'Preparing the run',
    icon: iconSitemap,
  },
  request_run_agent_now: {
    done: 'Prepared a live run',
    running: 'Preparing a live run',
    icon: iconPlay,
  },
  request_repeat: {
    done: 'Prepared the agent',
    running: 'Preparing the agent',
    icon: iconArrowsRotate,
  },
  run_one_off_play: {
    done: 'Started the run',
    running: 'Starting the run',
    icon: iconSitemap,
  },
  run_agent_now: {
    done: 'Started a live run',
    running: 'Starting a live run',
    icon: iconPlay,
  },
  repeat_one_off: {
    done: 'Made the agent',
    running: 'Making the agent',
    icon: iconArrowsRotate,
  },
  request_draft_agent: {
    done: 'Made a draft agent',
    running: 'Making a draft agent',
    icon: iconRobot,
  },
  create_draft_agent: {
    done: 'Made a draft agent',
    running: 'Making a draft agent',
    icon: iconRobot,
  },
};

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function count(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

/** What a repeat's records are (`records_noun`), one and many. */
const RECORD_NOUNS: Record<string, [string, string]> = {
  people: ['person', 'people'],
  leads: ['lead', 'leads'],
  companies: ['company', 'companies'],
};

/** A price a card shows as the coin and a number: "Up to" before it and a
 * short unit after it ("/lead", "/week"), never the word credits. */
export interface OneOffCost {
  amount: number;
  upTo?: boolean;
  unit?: 'lead' | 'company' | 'record' | 'run' | 'week';
}

/** One row of a confirmation card: a label, with words or a price at the
 * right. */
export interface OneOffRow {
  label: string;
  /** Words at the right: "Up to 50", a schedule. */
  value?: string;
  /** A price at the right, or Free. */
  cost?: OneOffCost | 'free';
  /** A consent step's raises against the saved play, a line each under it:
   *  "Can resend a post that may have gone out". */
  changes?: ReviewCardChangeLine[];
}

/** What a one-off confirmation card shows: a short title, its sentence, its
 * rows, the most it can spend and the balance. */
export interface OneOffCardView {
  /** The header, verb first: "Run once". */
  title: string;
  /** The main sentence: what it runs, on what. */
  text: string;
  /** The confirm button and its label once clicked. */
  verb: string;
  working: string;
  rows: OneOffRow[];
  /** The most it can spend, for the header; absent when it is free. */
  total?: OneOffCost;
  /** The balance, when the server sends it. */
  balance?: number;
  /** One gray line: "Stays off". */
  meta?: string;
  /** The consent split: the paid and outward steps Confirm consents to. */
  reviewRows?: OneOffRow[];
}

/** A consent split item as a row: what reaches outside the account, else
 * the most it charges a record (a run, for a brief). */
function reviewRow(item: ReviewCardItem): OneOffRow {
  const changes = reviewCardChanges(item);
  const withChanges = (row: OneOffRow): OneOffRow =>
    changes.length ? { ...row, changes } : row;
  if (item.reason === 'errors' || item.type === 'limits')
    return withChanges({ label: item.label });
  if (item.reason === 'outside')
    return withChanges({
      label: item.label,
      value: 'Reaches outside your account',
    });
  if (item.credits) {
    return withChanges({
      label: item.label,
      cost: {
        amount: item.credits,
        upTo: true,
        unit: item.type === 'agent_task' ? 'run' : 'record',
      },
    });
  }
  return withChanges({ label: item.label });
}

/** The most a card can spend for its header: none when it is free. */
function upTo(
  amount: number | undefined,
  unit?: OneOffCost['unit']
): OneOffCost | undefined {
  if (amount === undefined || amount <= 0) return undefined;
  return unit ? { amount, upTo: true, unit } : { amount, upTo: true };
}

/** Each confirmation's header, verb first: also the title of a card the
 * server sent without its card view. */
const ONE_OFF_TITLE: Record<string, string> = {
  run_one_off_play: 'Run once',
  run_agent_now: 'Run agent now',
  repeat_one_off: 'Make it repeat',
  create_draft_agent: 'Create draft agent',
};

export function oneOffTitle(action: string | undefined): string {
  return ONE_OFF_TITLE[action ?? ''] ?? 'Run once';
}

/** The card of a `run_one_off_play`, `run_agent_now`, `repeat_one_off` or
 * `create_draft_agent` confirmation, or null for any other. */
export function oneOffCardView(
  request: { tool_name?: string } & Record<string, unknown>
): OneOffCardView | null {
  const action = request.tool_name ?? '';
  if (!ONE_OFF_ACTIONS.has(action)) return null;

  if (action === 'run_one_off_play') {
    const card = asRecord(request.one_off);
    if (!card) return null;
    const unit = card.record_kind === 'company' ? 'company' : 'lead';
    const steps = Array.isArray(card.steps) ? card.steps : [];
    const rows: OneOffRow[] = steps.map((raw) => {
      const step = asRecord(raw) ?? {};
      const price = num(step.credits) ?? 0;
      return {
        label: text(step.label) ?? 'Step',
        cost: price > 0 ? { amount: price, unit } : 'free',
      };
    });
    const writes = Array.isArray(card.writes) ? card.writes : [];
    for (const write of writes) {
      if (typeof write === 'string' && write) rows.push({ label: write });
    }
    const balance = num(card.balance);
    const reviewItems = (
      Array.isArray(card.review_card) ? card.review_card : []
    ).filter(
      (item): item is ReviewCardItem =>
        typeof asRecord(item)?.nodeId === 'string' &&
        typeof asRecord(item)?.label === 'string'
    );
    const total = upTo(num(card.credits_max));
    return {
      title: oneOffTitle(action),
      text: text(card.title) ?? 'Run a play once',
      verb: 'Run',
      working: 'Running',
      rows,
      ...(total ? { total } : {}),
      ...(balance !== undefined ? { balance } : {}),
      ...(reviewItems.length ? { reviewRows: reviewItems.map(reviewRow) } : {}),
    };
  }

  if (action === 'run_agent_now') {
    const card = asRecord(request.run_now);
    if (!card) return null;
    const steps = Array.isArray(card.steps)
      ? card.steps.filter((s): s is string => typeof s === 'string')
      : [];
    const rows: OneOffRow[] = [];
    const trigger = text(card.trigger);
    if (trigger) rows.push({ label: 'Trigger', value: trigger });
    if (steps.length) rows.push({ label: 'Steps', value: steps.join(', ') });
    const records = num(card.records_max);
    if (records !== undefined) {
      rows.push({
        label: 'Records',
        value: `Up to ${records.toLocaleString()}`,
      });
    }
    // The steps that hold their records for someone's go ahead, one line each
    // ("Asks Jane first: CRM Action"): confirming this card is its confirmer's.
    const asksFirst = Array.isArray(card.asksFirst) ? card.asksFirst : [];
    for (const line of asksFirst) {
      if (typeof line === 'string' && line) rows.push({ label: line });
    }
    const balance = num(card.balance);
    const total = upTo(num(card.credits_max));
    return {
      title: oneOffTitle(action),
      text: text(card.title) ?? 'Start a live run',
      verb: 'Run',
      working: 'Running',
      rows,
      ...(total ? { total } : {}),
      ...(balance !== undefined ? { balance } : {}),
    };
  }

  if (action === 'create_draft_agent') {
    const card = asRecord(request.draft_agent);
    if (!card) return null;
    const brief = text(card.brief);
    return {
      title: oneOffTitle(action),
      text: text(card.title) ?? 'Create a draft agent',
      verb: 'Create',
      working: 'Creating',
      rows: brief ? [{ label: brief }] : [],
      meta: 'Stays off',
    };
  }

  const card = asRecord(request.repeat);
  if (!card) return null;
  const rows: OneOffRow[] = [];
  const source = text(card.source);
  if (source) rows.push({ label: 'Trigger', value: source });
  const schedule = text(card.schedule);
  if (schedule) rows.push({ label: 'Schedule', value: schedule });
  const steps = Array.isArray(card.steps)
    ? card.steps.filter((s): s is string => typeof s === 'string')
    : [];
  if (steps.length) rows.push({ label: 'Steps', value: steps.join(', ') });
  const records = num(card.records_per_run);
  const perRun = num(card.credits_per_run);
  if (records !== undefined && perRun !== undefined) {
    const [one, many] = RECORD_NOUNS[text(card.records_noun) ?? ''] ?? [
      'record',
      'records',
    ];
    rows.push({
      label: 'Each run',
      value: `Up to ${count(records, one, many)}`,
      cost: perRun > 0 ? { amount: perRun } : 'free',
    });
  }
  const total = upTo(num(card.credits_per_week), 'week');
  return {
    title: oneOffTitle(action),
    text: text(card.title) ?? 'Make it an agent',
    verb: 'Create agent',
    working: 'Creating',
    rows,
    ...(total ? { total } : {}),
    meta: 'Stays off',
  };
}

/** The run a confirmed card started: a one-off's (with its play and list)
 * or an agent's. */
export interface StartedRun {
  kind: 'one_off' | 'agent';
  runId: string;
  playId?: string;
  agentId?: string;
  listId?: string;
}

export function startedRunOf(
  displayType: string,
  display: unknown,
  result: unknown
): StartedRun | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const runId = text(shown.run_id) ?? text(data.run_id);
  if (!runId) return null;
  if (displayType === ONE_OFF_RUN_STARTED) {
    return {
      kind: 'one_off',
      runId,
      playId: text(shown.play_id) ?? text(data.play_id),
      listId: text(shown.list_id) ?? text(data.list_id),
    };
  }
  if (displayType === AGENT_RUN_STARTED) {
    return {
      kind: 'agent',
      runId,
      agentId: text(shown.agent_id) ?? text(data.agent_id),
    };
  }
  return null;
}

/** The agent a confirmed repeat made, or the draft agent a confirmed card
 * made: both come back with its id and name. */
export function repeatAgentOf(
  display: unknown,
  result: unknown
): { agentId: string; name: string } | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const agentId = text(shown.agent_id) ?? text(data.agent_id);
  if (!agentId) return null;
  return { agentId, name: text(shown.name) ?? text(data.name) ?? 'Agent' };
}

/** The draft agent `request_draft_agent` made and the chat its play is being
 * built in (none when the build didn't start), or null. */
export function draftAgentOf(
  display: unknown,
  result: unknown
): { agentId: string; name: string; chatId?: string } | null {
  const agent = repeatAgentOf(display, result);
  if (!agent) return null;
  const chatId =
    text(asRecord(display)?.conversation_id) ??
    text(asRecord(result)?.conversation_id);
  return chatId ? { ...agent, chatId } : agent;
}

/** Whether a message holds a draft agent's card, or the call still making
 * one: its chat opens the agent, so a page Clu opened in the same message
 * would only pull the user off it. */
export function hasDraftAgentPart(parts: readonly ThreadPartLike[]): boolean {
  return parts.some(
    (part) =>
      partDisplayType(part) === DRAFT_AGENT_CREATED ||
      (part.type === 'tool-call' &&
        asRecord(part.args)?.name === 'request_draft_agent')
  );
}

/** Every step type's name in the run card. Typed over every node type, so a
 * new one fails the build until it has a name here. */
const STEP_LABEL: Record<Exclude<CustomNodeType, 'trigger'>, string> = {
  enrich: 'Enrich',
  validate: 'Validate',
  find_people: 'Find people',
  get_company: 'Get company',
  find_open_jobs: 'Find open jobs',
  find_reviews: 'Find reviews',
  research: 'Agents',
  add_to_sequence: 'Add to sequence',
  export: 'Export to Google Sheets',
  create_google_doc: 'Create a Google Doc',
  send_mailbox_email: 'Send an email',
  send_linkedin_action: 'Send on LinkedIn',
  crm_action: 'CRM action',
  branch: 'Branch',
  approval: 'Approve',
  notify: 'Notify',
  webhook: 'Webhook',
  wait: 'Wait',
  code: 'Code',
  run_play: 'Hand off',
  find_meetings: 'Find meetings',
  job_history: 'Job history',
  review_duplicates: 'Review duplicates',
  merge_duplicates: 'Merge duplicates',
  fireflies_notes: 'Get Fireflies call notes',
  fathom_notes: 'Get Fathom call notes',
  gong_notes: 'Get Gong call notes',
  notion_add_page: 'Add a page to a Notion database',
  notion_update_page: 'Update a Notion page',
  identify_person: 'Identify person',
  match_company: 'Match company',
  count_people: 'Count people',
  instantly_capacity: 'Instantly capacity',
  agent_task: 'Brief',
};

const AGENT_LABEL: Record<string, string> = {
  'builtin:qualification': 'Qualification',
  'builtin:research': 'Research',
  [EMPLOYMENT_CHECK_SKILL_ID]: EMPLOYMENT_CHECK_NAME,
};

/** A step's name in the run card: the agents an Agents step runs, what an
 * Add to sequence step does in its sequence, else its type's label. */
function stepName(graph: CustomGraph, nodeId: string, type: string): string {
  const node = graph.nodes?.find((n) => n.id === nodeId);
  const config = asRecord(node?.config) ?? {};
  if (type === 'research' && Array.isArray(config.agentIds)) {
    const names = config.agentIds
      .filter((id): id is string => typeof id === 'string')
      .map((id) => AGENT_LABEL[id] ?? 'Custom agent');
    if (names.length) return names.join(' and ');
  }
  if (type === 'add_to_sequence') {
    const sequence = config as AddToSequenceConfig;
    const name = text(config.sequenceName)?.trim();
    // A pause or remove reads the way the card the user confirmed did
    // ("Remove from Live outbound"), never as an add.
    const change = sequenceChangeWords(sequence, name || 'sequence');
    if (change)
      return sequence.sequenceId ? sequenceStepSummary(sequence) : change;
    if (name) return 'Add to ' + name;
  }
  return STEP_LABEL[type as keyof typeof STEP_LABEL] ?? type;
}

/** The steps in walk order from the trigger (a branch's arms in edge order). */
function walkOrder(graph: CustomGraph): string[] {
  const nodes = graph.nodes ?? [];
  const edges = graph.edges ?? [];
  const trigger = nodes.find((n) => n.type === 'trigger');
  const order: string[] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    if (id !== trigger?.id) order.push(id);
    for (const edge of edges) if (edge.source === id) visit(edge.target);
  };
  if (trigger) visit(trigger.id);
  return order;
}

export interface RunStepView {
  id: string;
  name: string;
  /** "3 of 5", "Waiting". */
  progress: string;
  done: boolean;
}

/** Each step of a run as the run card lists it, in walk order: how many of
 * the records that reached it it has finished. */
export function runStepsView(run: CustomRunDetail): RunStepView[] {
  const stats = new Map<string, CustomRunNodeStats>(
    run.nodeStats.map((s) => [s.nodeId, s])
  );
  return walkOrder(run.graphSnapshot).map((id) => {
    const stat = stats.get(id);
    const node = run.graphSnapshot.nodes?.find((n) => n.id === id);
    const type = stat?.nodeType ?? node?.type ?? '';
    if (!stat || stat.entered === 0) {
      return {
        id,
        name: stepName(run.graphSnapshot, id, type),
        progress: 'Waiting',
        done: false,
      };
    }
    const finished = stat.succeeded + stat.failed + stat.filtered;
    return {
      id,
      name: stepName(run.graphSnapshot, id, type),
      progress: `${finished.toLocaleString()} of ${stat.entered.toLocaleString()}`,
      done: stat.status === 'completed' || finished >= stat.entered,
    };
  });
}
